import { computed, Injectable, signal } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, Subject } from 'rxjs';
import { GrocyBarcodesApi } from '../../../../core/api/grocy/barcodes.api';
import { GrocyProductActionsApi } from '../../../../core/api/grocy/product-actions.api';
import { GrocyProductsApi } from '../../../../core/api/grocy/products.api';
import { GrocyRegisteredPurchasesApi } from '../../../../core/api/grocy/registered-purchases.api';
import { GrocyStockApi } from '../../../../core/api/grocy/stock.api';
import { GrocyBarcodeBasics } from '../../../../core/entities/grocy/barcodes/barcode-basics.entity';
import { GrocyBarcodeRequest } from '../../../../core/entities/grocy/barcodes/barcode-request.entity';
import { GrocyProductAction } from '../../../../core/entities/grocy/product-actions/product-action.entity';
import { GrocyDashboardSubProduct } from '../../../../core/entities/grocy/products/dashboard-sub-product.entity';
import { GrocyRegisteredPurchaseBase } from '../../../../core/entities/grocy/registered-purchases/registered-purchase-base.entity';
import { GrocyRegisteredPurchaseRequest } from '../../../../core/entities/grocy/registered-purchases/registered-purchase-request.entity';
import { GrocyRegisteredPurchase } from '../../../../core/entities/grocy/registered-purchases/registered-purchase.entity';
import { GrocySuccess } from '../../../../core/entities/grocy/shared/success.entity';
import { ProductActionType } from '../../../../core/enums/product-actions/product-action-type.enum';
import { ApiErrorStrategy } from '../../../../core/enums/shared/api-error-strategy.enum';
import { StaticDataService } from '../../../../core/services/static-data.service';
import { GrocyDashboardProductForm } from '../../dashboard/forms/dashboard-product.form';
import { GrocyDashboardQuickActionConsumeForm } from '../../dashboard/forms/dashboard-quick-action-consume.form';
import { GrocyDashboardQuickActionOpenForm } from '../../dashboard/forms/dashboard-quick-action-open.form';
import { GrocyDashboardQuickActionTransferForm } from '../../dashboard/forms/dashboard-quick-action-transfer.form';
import { GrocyDashboardShoppingItemForm } from '../../dashboard/forms/dashboard-shopping-item.form';
import { GrocyDashboardShoppingSessionForm } from '../../dashboard/forms/dashboard-shopping-session.form';
import { GrocyDashboardForms } from '../../dashboard/models/dashboard-forms.model';
import { GrocyDashboardState } from '../../dashboard/models/dashboard-state.model';
import { GrocyDashboardSelectedProduct } from '../../dashboard/models/dashboard-selected-product.model';
import { formatCurrentStock } from '../../products/utils/product.utils';

@Injectable({
  providedIn: 'root',
})
export class GrocyDashboardService {
  public state = new GrocyDashboardState();

  public productSelected = computed(() => this.selectedProduct() !== undefined);
  public selectedProduct = signal<GrocyDashboardSelectedProduct | undefined>(undefined);
  public selectedProductName = computed(() => this.selectedProduct()?.name || '');

  public selectedProductQuantityUnit = computed(() =>
    this.state.productActionType === ProductActionType.Purchase
      ? this.selectedProduct()?.quantityUnits.purchase
      : this.selectedProduct()?.quantityUnits.stock
  );

  public selectedProductStockFormatted = computed(() =>
    this.productSelected() ? formatCurrentStock(this.selectedProduct()!) : ''
  );

  public forms: GrocyDashboardForms = {
    product: new GrocyDashboardProductForm(),
    quickAction: {
      consume: new GrocyDashboardQuickActionConsumeForm(),
      open: new GrocyDashboardQuickActionOpenForm(),
      transfer: new GrocyDashboardQuickActionTransferForm(),
    },
    shopping: {
      item: new GrocyDashboardShoppingItemForm(),
      session: new GrocyDashboardShoppingSessionForm(),
    },
  };

  submission: Subject<GrocyDashboardSelectedProduct | undefined> = new Subject<
    GrocyDashboardSelectedProduct | undefined
  >();

  constructor(
    private barcodesApi: GrocyBarcodesApi,
    private matSnackBar: MatSnackBar,
    private productActionsApi: GrocyProductActionsApi,
    private productsApi: GrocyProductsApi,
    private registeredPurchasesApi: GrocyRegisteredPurchasesApi,
    private staticDataService: StaticDataService,
    private stockApi: GrocyStockApi
  ) {
    this.staticDataService.getGrocyContextData().subscribe(grocyContextData => {
      this.forms.quickAction.transfer.options.grocyLocations = grocyContextData.locations;
      this.forms.shopping.item.options.grocyLocations = grocyContextData.locations;
    });
  }

  clearSelectedProduct(): void {
    this.clearForms(true);
    this.selectedProduct.set(undefined);
    this.state.isLoading = false;
  }

  private clearForms(clearProduct: boolean = false): void {
    if (clearProduct) {
      this.forms.product.clear();
    }

    this.forms.shopping.item.product = null;

    for (const quickActionForm of Object.values(this.forms.quickAction)) {
      quickActionForm.clear();
    }
  }

  findBarcode(barcode: string): void {
    this.disableForms();
    this.state.isLoadingByBarcode = true;

    this.productsApi
      .findOneSubProductByBarcodeDashboard(barcode, ApiErrorStrategy.silent)
      .subscribe((product: GrocyDashboardSubProduct | null) => {
        if (!product) {
          this.clearForms();
          this.selectedProduct.set(undefined);
          this.submission.next(undefined);
          this.state.isSubmitting = false;

          setTimeout(() => {
            this.state.isLoading = false;
            this.enableForms();
          }, 500);

          return;
        }

        this.forms.product.options.grocyProducts = [];
        this.forms.product.controls.product.setValue(product.name);
        this.forms.product.barcodeExists = true;
        this.loadSelectedProduct(product);
      });
  }

  findDetailedProductByProductId(productId: number): void {
    if (this.forms.product.barcodeExists) {
      this.forms.product.controls.barcode.setValue(null);
    }

    this.disableForms();
    this.state.isLoadingByProductId = true;

    this.productsApi.findOneSubProductByIdDashboard(productId).subscribe((product: GrocyDashboardSubProduct) => {
      this.loadSelectedProduct(product);
    });
  }

  loadSelectedProduct(product: GrocyDashboardSubProduct): void {
    const selectedProduct: GrocyDashboardSelectedProduct = product;

    this.forms.shopping.item.clear();
    this.forms.shopping.item.product = product;

    this.selectedProduct.set(selectedProduct);

    for (const quickActionForm of Object.values(this.forms.quickAction)) {
      quickActionForm.selectedProduct = selectedProduct;
    }

    if (this.state.isSubmitting) {
      this.submission.next(selectedProduct);
      this.state.isSubmitting = false;
    }

    setTimeout(() => {
      this.state.isLoading = false;
      this.enableForms();
    }, 500);
  }

  submitQuickAction(productAction: GrocyProductAction): Observable<GrocySuccess> {
    this.disableForms();
    this.state.isSubmitting = true;

    return new Observable(subscriber => {
      this.productActionsApi.execute(this.state.productActionType, productAction).subscribe((success: GrocySuccess) => {
        subscriber.next(success);
        subscriber.complete();
        this.postProcessProductAction(productAction);
      });
    });
  }

  submitRegisteredPurchase(): Observable<GrocyRegisteredPurchase[]> {
    this.disableForms();
    this.state.isSubmitting = true;

    return new Observable(subscriber => {
      const registeredPurchase: GrocyRegisteredPurchaseBase = {
        actualPrice: this.forms.shopping.item!.actualPrice,
        amount: this.forms.shopping.item!.amount,
        fullPrice: this.forms.shopping.item!.fullPrice,
        productId: this.selectedProduct()!.id,
        productName: this.selectedProductName(),
        totalDiscount: this.forms.shopping.item!.totalDiscount,
        totalPant: this.forms.shopping.item!.totalPant,
        unitPant: this.forms.shopping.item!.purchaseQuPant,
        unitPrice: this.forms.shopping.item!.purchaseQuPrice,
      };

      const productAction = this.forms.shopping.item!.getProductAction(
        this.forms.shopping.session.date!,
        this.forms.shopping.session.store!
      );

      const request: GrocyRegisteredPurchaseRequest = { ...registeredPurchase, purchaseProduct: productAction };

      this.registeredPurchasesApi
        .registerPurchase(request)
        .subscribe((registeredPurchases: GrocyRegisteredPurchase[]) => {
          subscriber.next(registeredPurchases);
          this.postProcessProductAction(productAction);
        });
    });
  }

  undoTransaction(transactionId: string): Observable<GrocySuccess> {
    this.state.isSubmitting = true;

    return new Observable(observable => {
      this.stockApi.undoTransaction(transactionId).subscribe((success: GrocySuccess) => {
        this.matSnackBar.open(success.message, 'Dismiss', { duration: 5000 });
        this.findDetailedProductByProductId(this.selectedProduct()!.id);
        observable.next(success);
        observable.complete();
      });
    });
  }

  private get barcode(): GrocyBarcodeRequest {
    const selectedProduct = this.selectedProduct()!;

    return {
      productId: selectedProduct.id,
      barcode: this.forms.product.barcode!,
      quantityUnit: this.selectedProductQuantityUnit()!,
      amount: 1,
      store: selectedProduct.store!,
      note: '',
    };
  }

  private constructProductActionSnackbarMessage(success: boolean, { amount }: GrocyProductAction): string {
    const quantityUnit = this.selectedProductQuantityUnit()!;

    const quantityUnitString = amount === 1 ? quantityUnit.name : quantityUnit.namePlural;

    const message = `${this.state.productActionType.toLowerCase()} of ${amount} ${quantityUnitString} ${this.selectedProductName()}`;

    return success ? `Succeeded ${message}` : `Failed to execute ${message}`;
  }

  private postProcessProductAction(productAction: GrocyProductAction): void {
    let message = this.constructProductActionSnackbarMessage(true, productAction);

    if (this.forms.product.barcodeExists || !this.forms.product.barcode) {
      this.matSnackBar.open(message, 'Dismiss', { duration: 5000 });
      this.findDetailedProductByProductId(productAction.productId!);
    } else {
      this.barcodesApi.create(this.barcode).subscribe((response: GrocyBarcodeBasics | null) => {
        const productName = this.selectedProductName();

        message += response
          ? ` and successfully linked ${this.barcode.barcode} to ${productName}`
          : ` and failed to link ${this.barcode.barcode} to ${productName}`;

        this.matSnackBar.open(message, 'Dismiss', { duration: 5000 });
        this.findDetailedProductByProductId(productAction.productId!);
      });
    }
  }

  private disableForms(): void {
    this.forms.product.disable();
    this.forms.quickAction.consume.disable();
    this.forms.quickAction.open.disable();
    this.forms.quickAction.transfer.disable();
    this.forms.shopping.item.disable();
  }

  private enableForms(): void {
    this.forms.product.enable();
    this.forms.quickAction.consume.enable();
    this.forms.quickAction.open.enable();
    this.forms.quickAction.transfer.enable();
    this.forms.shopping.item.enable();
  }
}
